import { CalendarToday } from '@mui/icons-material';
import { Box, Popper } from '@mui/material';
import Paper from '@mui/material/Paper';
import moment from "moment";
import { useEffect, useState } from 'react';
import { DateRangePicker } from "react-dates";
import 'react-dates/initialize';
import { createGlobalStyle } from "styled-components";
import { useTranslation } from '../../utility/function/useTranslations';
import './RangeDateControl.css';

/** Last selectable check-in day = today + 364 days. Last selectable check-out day = today + 365 days. */
const MAX_CHECKIN_DAYS_FROM_TODAY = 364;
const MAX_CHECKOUT_DAYS_FROM_TODAY = 365;

/** Sunday-first day header keys in `common` locale JSON. */
const CALENDAR_DAY_HEADER_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

const ARABIC_MONTHS = [
  'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
];

/** Must run before react-dates renders; useEffect is too late (week headers cached on first open). */
function applyWidgetMomentDayLabels(locale: string, getCommonLabel: (key: string) => string) {
  const loc = locale || 'en';
  const momentLocaleId = `ibe-widget-${loc}`;
  const weekdays = CALENDAR_DAY_HEADER_KEYS.map((key) => getCommonLabel(key));
  const localeConfig: moment.LocaleSpecification = {
    parentLocale: 'en',
    weekdays,
    weekdaysShort: weekdays,
    weekdaysMin: weekdays,
    week: { dow: 0, doy: 6 },
    ...(loc === 'ar' ? { months: ARABIC_MONTHS, monthsShort: ARABIC_MONTHS } : {}),
  };

  if (moment.locales().includes(momentLocaleId)) {
    moment.updateLocale(momentLocaleId, localeConfig);
  } else {
    moment.defineLocale(momentLocaleId, localeConfig);
  }
  moment.locale(momentLocaleId);
}

const RangeDateControl = (props: any) => {
  const { selectedDate, handleDateChange, touched, errors, isMobile = false, fontColor, booknowbuttoncolor, booknowbuttonfontcolor, booknowbuttonhovercolor, buttonHoverFontColor, setEventType, eventType, barData, setStartDate, navStartDate, demandData, barCurrency, isDemandCalendar, destinationType = { type: "0" }, propertyID, currencyMappingData, isMultiCurrencyFFEnable, preferredCurrency, hideCheckout = false, locale } = props;
  const [startDate, endDate] = selectedDate;
  const [focusedInput, setFocusedInput] = useState<any>(null); // Changed from isMobile?null:"startDate" to null
  const [open, setOpen] = useState(false); // Changed from true to false
  const [boxHidden, setBoxHidden] = useState(false);
  const [anchorEl, setAnchorEl] = useState<any>(null);
  const [rateLegend, setRateLegend] = useState<any[]>([]);
  const { t } = useTranslation();
  const activeLocale = locale || 'en';
  applyWidgetMomentDayLabels(activeLocale, (key) => t('common', key, activeLocale));

  useEffect(() => {
    // Call dropDownPosition() when the component is initially rendered
    dropDownPosition();
    const handleScroll = () => dropDownPosition();
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);

    // Prevent automatic opening on initial load
    if (isMobile) {
      setOpen(false)
    } else {
      // Delay setting focusedInput to prevent automatic opening
      const timer = setTimeout(() => {
        setFocusedInput("startDate");
      }, 2000); // 2 second delay

      return () => clearTimeout(timer);
    }

    if (!isMobile && document.getElementsByClassName("DayPicker_transitionContainer")) {
      let ele = document.getElementsByClassName("DayPicker_transitionContainer")[0];
      setAnchorEl(ele);
    }
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (barData.length > 0) {
      const legends: any[] = [];
      let num = 0;
      while (legends.length <= 2 && num < barData.length) {
        let barDayData = barData[num];
        let price = (barCurrency !== preferredCurrency?.code && currencyMappingData?.length > 0) ? convertPriceToPreferredCurrencyNonMemo(barDayData?.rate, barCurrency, false, true) : barDayData?.rate ? barDayData?.rate : 0;
        if (price >= 10000 && !legends.includes("k")) {
          legends.push("k");
        }
        if (price >= 1000000 && !legends.includes("m")) {
          legends.push("m");
        }
        num++;
      }
      if (JSON.stringify(rateLegend) !== JSON.stringify(legends)) {
        setRateLegend(legends);
      }
    }

  }, [barData, propertyID, preferredCurrency])

  function getLastDateOfNextMonth(dateStr: string) {
    let date = new Date(dateStr);
    let year = date.getFullYear();
    let month = date.getMonth();
    let nextMonth = month + 2;
    if (nextMonth > 11) {
      year += 1;
      nextMonth -= 12;
    }
    let firstDayOfNextMonth: any = new Date(year, nextMonth, 1);
    let lastDayOfNextMonth: any = new Date(firstDayOfNextMonth - 1);
    let formattedDate = lastDayOfNextMonth.toISOString().split('T')[0];
    return formattedDate;
  }

  function increaseDateByTwoMonths(dateString: any) {
    // Handle different input types
    if (!dateString) return '';

    let parts: string[];
    if (typeof dateString === 'string') {
      parts = dateString.split('-');
    } else {
      // Handle Date objects or other types
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      parts = [year.toString(), month, day];
    }

    if (parts.length !== 3) return '';

    var year = parseInt(parts[0]);
    var month = parseInt(parts[1]);
    var day = parseInt(parts[2]);
    month += 2;
    if (month > 12) {
      year += Math.floor((month - 1) / 12);
      month = (month - 1) % 12 + 1;
    }
    var isLeapYear = (year % 4 == 0 && year % 100 != 0) || (year % 400 == 0);
    var daysInMonth = [31, (isLeapYear ? 29 : 28), 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if (day > daysInMonth[month - 1]) {
      day = daysInMonth[month - 1];
    }
    var newDateString = year + '-' + (month < 10 ? '0' + month : month) + '-' + (day < 10 ? '0' + day : day);
    return newDateString;
  }

  const isDateWithinBookingWindow = (dateStr: string) => true;

  const handleNavigationButtonClick = () => {
    if (destinationType?.type == "2") {
      if (navStartDate.length == 1) {
        if (navStartDate[0].date != null) {
          if (navStartDate[0].fetch) {
            let _date = increaseDateByTwoMonths(navStartDate[0].date);
            if (isDateWithinBookingWindow(_date)) {
              setStartDate([...navStartDate, { date: _date, fetch: true }]);
            }
          } else {
            let _date = increaseDateByTwoMonths(navStartDate[0].date);
            if (isDateWithinBookingWindow(_date)) {
              setStartDate([{ date: _date, fetch: true }]);
            }
          }
        } else {
          const today = new Date();
          const year = today.getFullYear();
          const month = String(today.getMonth() + 1).padStart(2, '0');
          const day = String(today.getDate()).padStart(2, '0');
          let _date = selectedDate.length > 0 && Date.parse(selectedDate[1]) >= today.setMonth(today.getMonth() + 2) ? getLastDateOfNextMonth(selectedDate[1]) : increaseDateByTwoMonths(`${year}-${month}-${day}`);
          if (isDateWithinBookingWindow(_date)) {
            setStartDate([{ date: _date, fetch: true }]);
          }
        }
      } else {
        let _date = increaseDateByTwoMonths(navStartDate[navStartDate.length - 1].date);
        if (isDateWithinBookingWindow(_date)) {
          setStartDate([...navStartDate, { date: _date, fetch: true }]);
        }
      }
    }
  };

  const compareDates = (d1: any, d2: any) => {
    var date1 = new Date(d1._d);
    var date2 = d2 ? new Date(d2) : new Date();
    var year1 = date1.getFullYear();
    var month1 = date1.getMonth();
    var day1 = date1.getDate();
    var year2 = date2.getFullYear();
    var month2 = date2.getMonth();
    var day2 = date2.getDate();
    if (year1 < year2) {
      return -1;
    } else if (year1 > year2) {
      return 1;
    }

    if (month1 < month2) {
      return -1;
    } else if (month1 > month2) {
      return 1;
    }

    if (day1 < day2) {
      return -1;
    } else if (day1 > day2) {
      return 1;
    }

    // If all components are equal, dates are equal
    return 0;
  }

  /**
   * Replaces react-dates default isOutsideRange (past dates only).
   * - Check-in: selectable from today through today + 364 days.
   * - Check-out: selectable from day after check-in through today + 365 days.
   */
  const isOutsideSelectableRange = (day: moment.Moment) => {
    const d = day.clone().startOf('day');
    const today = moment().startOf('day');
    // if (d.isBefore(today)) return true;

    // if (hideCheckout) {
    //   const maxCheckIn = today.clone().add(MAX_CHECKIN_DAYS_FROM_TODAY, 'days');
    //   return d.isAfter(maxCheckIn);
    // }

    // if (focusedInput === 'endDate') {
    //   const maxCheckOut = today.clone().add(MAX_CHECKOUT_DAYS_FROM_TODAY, 'days');
    //   return d.isAfter(maxCheckOut);
    // }

    // const maxCheckIn = today.clone().add(MAX_CHECKIN_DAYS_FROM_TODAY, 'days');
    // return d.isAfter(maxCheckIn);
    return d.isBefore(today)
  };

  const bookingMinDate = () => moment().startOf('day');
  /** Upper bound for calendar month navigation (matches furthest bookable month). */
  // const bookingMaxDateForPicker = () =>
  //   moment()
  //     .startOf('day')
  //     .add(hideCheckout ? MAX_CHECKIN_DAYS_FROM_TODAY : MAX_CHECKOUT_DAYS_FROM_TODAY, 'days');

  const isDayBlocked = (day: any) => {
    if (focusedInput == "endDate") {
      return false;
    }
    if (destinationType?.type == "2") {
      let barDayData = barData.length > 0 ? barData[barData.findIndex((ele: any) => compareDates(day, ele.date) == 0)] : null;
      if (barDayData == null) {
        return false;
      }
      if (barDayData?.inventory <= 0 || barDayData?.rate == 0) {
        return true;
      }
    }
    return false;
  };

  function formatNumber(num: number) {
    num = Math.ceil(num);
    if (num >= 1000000000) {
      // If the number is 1 billion or more
      return (num / 1000000000).toFixed(1) + 'B';
    } else if (num >= 1000000) {
      // If the number is 1 million or more
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 10000) {
      // If the number is 10 thousand or more
      return (num / 1000).toFixed(1) + 'K';
    } else {
      // If the number is less than 10 thousand
      return num;
    }
  }

  const convertPriceToPreferredCurrencyNonMemo = (price: any, currencyType: any, isFixedToDecimal: boolean = false, convertToPreferredCurrency = true) => {
    if (isMultiCurrencyFFEnable && convertToPreferredCurrency) {
      const preferredCurrencyConfig = currencyMappingData?.find((x: any) => x.targetCurrencyCode === preferredCurrency?.code);

      // When we have the target currency and currency is same
      if (preferredCurrencyConfig?.targetCurrencyCode === currencyType) {
        if (isFixedToDecimal) {
          return parseFloat(price?.toString()).toFixed(2);
        }
        return price;
      }
      else {
        // This if block execute when we no currency selected in dropdown
        if (!preferredCurrencyConfig?.targetCurrencyCode) {
          return price;
        }
        else {
          const ConversionRatioForPropertyCurrency = currencyMappingData?.find((x: { targetCurrencyCode: any; }) => x.targetCurrencyCode === currencyType);
          const propertyCurrencyConversion = price / ConversionRatioForPropertyCurrency?.conversionRate;
          //console.log("🚀 ~ convertPriceToPreferredCurrency ~ propertyCurrencyConversion:", propertyCurrencyConversion)
          const preferredCurrencyConversion = propertyCurrencyConversion * preferredCurrencyConfig?.conversionRate;
          //console.log("🚀 ~ convertPriceToPreferredCurrency ~ preferredCurrencyConversion:", preferredCurrencyConversion)
          if (isFixedToDecimal) {
            return parseFloat(preferredCurrencyConversion.toString()).toFixed(2);
          }
          return preferredCurrencyConversion;
        }
      }
    }
    else {
      if (isFixedToDecimal) {
        return parseFloat(price?.toString()).toFixed(2);
      }
      return price;
    }
  };

  const renderDayContents = (day: any) => {
    if (compareDates(day, null) == -1) { //for dates less than the todays date.
      return (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {String(Number(day.format('D')).toLocaleString(locale == "ar" ? "ar-EG" : "en"))}
          <span style={{ fontSize: "8px", opacity: "0.5", marginTop: "1px" }}>--</span>
        </div>
      )
    }
    if (!isDemandCalendar || isOutsideSelectableRange(day)) {
      return (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {String(Number(day.format('D')).toLocaleString(locale == "ar" ? "ar-EG" : "en"))}
          <span style={{ fontSize: "8px", opacity: "0.5", marginTop: "1px" }}>{ }</span>
        </div>
      );
    }
    if (destinationType?.type == '1') { //for search where the destination is CITY, so no data for bar is coming
      let demandDayData = demandData.length > 0 ? demandData[demandData.findIndex((ele: any) => compareDates(day, ele.checkinDate) == 0)] : {};
      return (
        <div style={{ display: "flex", flexDirection: "column", color: demandDayData?.demandIndex > 90 ? "#EB2727" : "" }}>
          {String(Number(day.format('D')).toLocaleString(locale == "ar" ? "ar-EG" : "en"))}
          <span style={{ fontSize: "8px", opacity: "0.5", marginTop: "1px" }}>{ }</span>
        </div>
      );
    } else if (destinationType?.type == '2') { //for search where the destination is PROPERTY, so data for bar and demand is coming
      let barDayData = barData.length > 0 ? barData[barData.findIndex((ele: any) => compareDates(day, ele.date) == 0)] : null;
      if (barDayData != null && (barDayData?.inventory <= 0 || barDayData?.rate == 0) && focusedInput != "endDate") {
        return (
          <div style={{ display: "flex", flexDirection: "column", backgroundColor: "lightgrey", opacity: 0.4, padding: "2px" }}>
            {String(Number(day.format('D')).toLocaleString(locale == "ar" ? "ar-EG" : "en"))}
            <span style={{ fontSize: "8px", opacity: "0.5", marginTop: "1px" }}>--</span>
          </div>
        );
      }
      let demandDayData = demandData.length > 0 ? demandData[demandData.findIndex((ele: any) => compareDates(day, ele.checkinDate) == 0)] : {};
      let price: any = barDayData != null && barDayData?.rate ? (barCurrency !== preferredCurrency?.code && currencyMappingData?.length > 0) ? formatNumber(convertPriceToPreferredCurrencyNonMemo(barDayData?.rate, barCurrency, false, true)) : formatNumber(barDayData?.rate) : "";
      return (
        <div style={{ display: "flex", flexDirection: "column", color: demandDayData?.demandIndex > 90 ? "#EB2727" : "" }}>
          {String(Number(day.format('D')).toLocaleString(locale == "ar" ? "ar-EG" : "en"))}
          <span style={{ fontSize: "8px", opacity: "0.5", marginTop: "1px" }}>{price == "NaN" ? "--" : typeof (price) == "string" && price?.includes("K") ? String(Number(price.split("K")[0]).toLocaleString(locale == "ar" ? "ar-EG" : "en")) + `${t("widget", "K", locale)}` : typeof (price) == "string" && price?.includes("M") ? String(Number(price.split("M")[0]).toLocaleString(locale == "ar" ? "ar-EG" : "en")) + `${t("widget", "M", locale)}` : String(Number(price).toLocaleString(locale == "ar" ? "ar-EG" : "en"))}</span>
        </div>
      );
    } else { //for search where the destination is COUNTRY, so no data for bar and demand is coming
      return (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {String(Number(day.format('D')).toLocaleString(locale == "ar" ? "ar-EG" : "en"))}
          <span style={{ fontSize: "8px", opacity: "0.5", marginTop: "1px" }}>{ }</span>
        </div>
      );
    }
  };

  useEffect(() => {
    // Prevent automatic focus during initial load
    const timeSinceLoad = Date.now() - window.performance.timing.navigationStart;
    if (timeSinceLoad < 2000) {
      return;
    }

    if (eventType === '' && !isMobile) {
      setOpen(false);
      setFocusedInput("startDate");
    }
    if (!isMobile && eventType == "onMouseClick" && open) {
      let ele = document.getElementsByClassName("DayPicker_transitionContainer")[0];
      setAnchorEl(ele);
    }
  }, [eventType]);

  const handleFocusChange = (_focusedInput: any) => {
    // Prevent focus changes during initial load (first 2 seconds)
    const timeSinceLoad = Date.now() - window.performance.timing.navigationStart;
    if (timeSinceLoad < 2000) {
      return;
    }

    if (isMobile) {
      setFocusedInput(_focusedInput);
    } else {
      if (_focusedInput == null) {
        if (open) {
          setEventType("");
        }
        setOpen(false);
        setFocusedInput(null);
        // Remove the automatic reopening logic
        // setTimeout(()=>{
        //   setFocusedInput("startDate");
        // },1)
      } else {
        setFocusedInput(_focusedInput);
      }
    }
  };

  useEffect(() => {
    if (focusedInput != null) {
      if (isMobile && document.getElementsByClassName("DayPicker_transitionContainer")) {
        let ele = document.getElementsByClassName("DayPicker_transitionContainer")[0];
        setAnchorEl(ele);
      }
    } else {
      setAnchorEl(null);
    }
  }, [focusedInput])

  const getDate = (date: any): any => {
    if (!date) return null;

    let formattedDate: string;

    if (typeof date === 'string') {
      // If it's already a string, use it directly
      formattedDate = dateObject(date);
    } else if (date instanceof Date) {
      // If it's a Date object, format it directly
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      formattedDate = `${year}-${month}-${day}`;
    } else {
      // Handle other types
      formattedDate = dateObject(date);
    }

    // Parse the date string directly to avoid timezone issues
    const parts = formattedDate.split('-');
    if (parts.length === 3) {
      const year = parseInt(parts[0]);
      const month = parseInt(parts[1]) - 1; // months are zero-indexed
      const day = parseInt(parts[2]);
      const localDate = new Date(year, month, day);
      return moment(localDate);
    }

    // Fallback
    return moment(new Date(formattedDate));
  };

  const dateObject = (date: any) => {
    if (!date) return '';

    let parts: string[];
    if (typeof date === 'string') {
      parts = date.split('-');
    } else {
      // Handle Date objects or other types
      const dateObj = new Date(date);
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      parts = [year.toString(), month, day];
    }

    let dateObject = parts.map((item: string | any[]) => {
      if (item.length < 2) {
        return `0${item}`
      } return item
    })
    return dateObject.join('-');
  }

  const dropDownPosition = () => {
    const boxElement = document.getElementById('IBE_Calendar_Pos');
    if (!boxElement) return;
    const boxPosition = boxElement.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    if (boxPosition.bottom < 0 || (boxPosition.top + boxPosition.bottom) > windowHeight) {
      setBoxHidden(true);
    } else {
      setBoxHidden(false);
    }
  }

  const GlobalStyles = createGlobalStyle`
        .widgetDateRange .DateRangePickerInput_arrow svg, .widgetDateRange .startDate-icon-widget svg{
            color: ${fontColor} !important;
        }
        .widgetDateRange .DateInput.DateInput_1::before  {
            color: ${fontColor} !important;
        }
        #checkInDateWidget, #checkOutDateWidget {
            color: ${fontColor} !important;
        }
        #checkInDateWidget:focus-visible, #checkOutDateWidget:focus-visible {
          outline: none
        }
        .CalendarDay__selected,
        .CalendarDay__selected:active {
          background: ${booknowbuttoncolor} !important;
          border: 1px double ${booknowbuttoncolor} !important;
          color: ${booknowbuttonfontcolor} !important;
        }
        .CalendarDay__selected:hover {
          background:  ${isMobile ? booknowbuttoncolor : booknowbuttonhovercolor} !important;
          border: 1px double ${isMobile ? booknowbuttoncolor : booknowbuttonhovercolor} !important;
          color: ${isMobile ? booknowbuttonfontcolor : buttonHoverFontColor} !important;
        }
        .CalendarDay__selected_span, .CalendarDay__selected_span:active {
          background: ${booknowbuttoncolor} !important;
          border: 1px double ${booknowbuttoncolor};
          opacity: 50%;
          color: ${booknowbuttonfontcolor} !important;
        }
        .CalendarDay__selected_span:hover {
          background: ${booknowbuttonhovercolor} !important;
          border: 1px double ${booknowbuttonhovercolor} !important;
          opacity: 100%;
        }
        .CalendarDay__default:hover {
          background: ${isMobile ? booknowbuttoncolor : booknowbuttonhovercolor};
          color: ${isMobile ? booknowbuttonfontcolor : buttonHoverFontColor} !important;
        }
        .CalendarMonth_caption {
          color: black !important;
        }
        .CalendarDay__default {
          color: black;
        }
        .CalendarDay__blocked_out_of_range, .CalendarDay__blocked_out_of_range:active, .CalendarDay__blocked_out_of_range:hover {
          opacity : 50%;
          color: black !important;
        }
        .CalendarDay__blocked_out_of_range:hover {
          background: none;
        }
        .DayPicker_weekHeader_ul {
          color: black !important;
        }
        .widgetDateRange .DateInput.DateInput_1:first-of-type::before {
          content: "${t("common", "checkin", locale)}" !important;
        }
        .widgetDateRange .DateInput.DateInput_1:not(:first-of-type)::before {
          content: "${hideCheckout ? "" : t("common", "checkout", locale)}" !important;
        }
        ${hideCheckout ? `
          .widgetDateRange .DateInput.DateInput_1:not(:first-of-type) {
            display: none !important;
          }
          .widgetDateRange .DateRangePickerInput_arrow {
            display: none !important;
          }
        ` : ''}
        ${locale == "ar" && `
          .widgetDateRange .DateInput.DateInput_1:first-of-type{
            border-right: none !important;
            border-left: 1px solid #ffffff42 !important;
          }`}
    `;

  return (
    <Box component="div" id="Calendar-container" onClick={() => { if (!open) setOpen(true) }}>
      <GlobalStyles />
      <Box component="div" className={`relative ${isMobile ? "widgetDateRange" : open ? "openWidget widgetDateRange" : "closeWidget widgetDateRange"} ${hideCheckout ? " hide-checkout" : ""} ${locale == "ar" ? "pr-2" : ""}`} sx={{ height: '100%' }}>
        <Box component="div" className="calenderIcon-widget startDate-icon-widget" id="IBE_Calendar_Pos">
          <CalendarToday sx={{ fontSize: '1.1rem !important', color: fontColor ? `${fontColor} important` : "#fff" }}></CalendarToday>
        </Box>
        {hideCheckout ? (
          <DateRangePicker
            key={`date-picker-${locale}-hide-checkout`}
            startDateId="checkInDateWidget"
            endDateId="checkOutDateWidget"
            startDate={getDate(startDate)}
            endDate={null}
            onDatesChange={(value) => {
              handleDateChange({ ...value, endDate: value.startDate });
              // Close the picker after date selection for day booking
              if (value.startDate) {
                setTimeout(() => {
                  setFocusedInput(null);
                  setOpen(false);
                  setEventType("");
                }, 0);
              }
            }}
            focusedInput={focusedInput}
            onFocusChange={handleFocusChange}
            readOnly={true}
            customArrowIcon={<CalendarToday />}
            hideKeyboardShortcutsPanel={true}
            displayFormat="DD MMM, YY"
            startDateAriaLabel="Check-In"
            startDatePlaceholderText="check-In"
            numberOfMonths={isMobile ? 1 : 2}
            anchorDirection={'left'}
            openDirection='up'
            renderDayContents={renderDayContents}
            onNextMonthClick={() => handleNavigationButtonClick()}
            isOutsideRange={isOutsideSelectableRange}
            minDate={bookingMinDate()}
            isDayBlocked={isDayBlocked}
            isRTL={locale == "ar"}
          />
        ) : (
          <DateRangePicker
            key={`date-picker-${locale}`}
            startDateId="checkInDateWidget"
            endDateId="checkOutDateWidget"
            startDate={getDate(startDate)}
            endDate={getDate(endDate)}
            onDatesChange={(value) => handleDateChange(value)}
            focusedInput={focusedInput}
            onFocusChange={handleFocusChange}
            readOnly={true}
            customArrowIcon={<CalendarToday />}
            hideKeyboardShortcutsPanel={true}
            displayFormat="DD MMM, YY"
            startDateAriaLabel="Check-In"
            startDatePlaceholderText="check-In"
            endDatePlaceholderText="check-Out"
            numberOfMonths={isMobile ? 1 : 2}
            anchorDirection={'left'}
            openDirection='up'
            renderDayContents={renderDayContents}
            onNextMonthClick={() => handleNavigationButtonClick()}
            isOutsideRange={isOutsideSelectableRange}
            minDate={bookingMinDate()}
            isDayBlocked={isDayBlocked}
            isRTL={locale == "ar"}
          />
        )}
        {hideCheckout && (
          <Box
            sx={{
              position: 'absolute',
              top: isMobile ? '51px' : '66%',
              transform: 'translateY(-50%)',
              fontSize: '12px',
              color: `${booknowbuttonfontcolor} !important`,
              backgroundColor: 'transparent',
              padding: '0 4px',
              pointerEvents: 'none',
              zIndex: 0,
            }}
            style={locale == "ar" ? {marginRight: '124px'} : {marginLeft: '120px'}}
          >
            ({t("widget", "sameDayCheckout", locale)})
          </Box>
        )}
        {open && anchorEl != null && isDemandCalendar && (barData.length > 0 || demandData.length > 0) &&
          <Popper id='simple-popper' open={open} anchorEl={anchorEl} placement='bottom-end' sx={{ zIndex: "1300", inset: isMobile ? "-2px 0px auto auto !important" : "-4px -1px auto auto !important" }}>
            {isMobile ?
              <Paper sx={{
                width: "318.5px",
                borderRadius: "0px 0px 5px 5px",
                boxShadow: "unset",
                display: (open && anchorEl != null) ? "block !important" : "none !important",
                border: "1px solid hsl(214.3 31.8% 91.4%)",
                borderTop: "none"
              }}>
                <div style={{ display: "flex", flexDirection: "row-reverse", padding: "10px 14px 10px 0px", backgroundColor: "#FAFAFA" }}>
                  {barData.length > 0 && barCurrency && <span style={{ fontSize: "8px", color: "#8A8C9A", marginLeft: "8px" }}>{t("widget", "allRates", locale)} {preferredCurrency?.code ? preferredCurrency?.code : barCurrency}</span>}
                  {rateLegend.includes("k") && destinationType.type == "2" && <span style={{ fontSize: "8px", color: "#8A8C9A", marginLeft: "8px" }}>{t("widget", "K", locale)} = {String(Number(1000).toLocaleString(locale == "ar" ? "ar-EG" : "en"))}</span>}
                  {rateLegend.includes("m") && destinationType.type == "2" && <span style={{ fontSize: "8px", color: "#8A8C9A", marginLeft: "8px" }}>{t("widget", "M", locale)} = {String(Number(1000000).toLocaleString(locale == "ar" ? "ar-EG" : "en"))}</span>}
                  {demandData.length > 0 &&
                    <div style={{ display: "flex", marginLeft: "8px" }}>
                      <span style={{ fontSize: "8px", color: "#EB2727" }}>{t("widget", "highDemand", locale)}</span>
                      <span style={{ fontSize: "8px", color: "#8A8C9A", marginLeft: "2px" }}>{t("widget", "dates", locale)}</span>
                    </div>
                  }
                  {barData.length > 0 &&
                    <div style={{ display: "flex" }}>
                      <div style={{ width: "8px", height: "8px", backgroundColor: "#EBEBEB", borderRadius: "2px", marginTop: "1px" }} />
                      <span style={{ fontSize: "8px", color: "#8A8C9A", marginLeft: "2px" }}>{t("widget", "soldOut", locale)}</span>
                    </div>
                  }
                </div>
              </Paper>
              :
              <Paper sx={{
                width: "619px",
                borderRadius: "0px 0px 5px 5px",
                boxShadow: "unset",
                display: (open && anchorEl != null) ? "block !important" : "none !important",
                border: "1px solid hsl(214.3 31.8% 91.4%)",
                borderTop: "none"
              }}>
                <div style={{ display: "flex", flexDirection: "row-reverse", padding: "10px 20px 10px 0px", backgroundColor: "#FAFAFA" }}>
                  {barData.length > 0 && barCurrency && <span style={{ fontSize: "11px", color: "#8A8C9A", marginLeft: "14px" }}>{t("widget", "allRates", locale)} {barCurrency}</span>}
                  {rateLegend.includes("k") && destinationType.type == "2" && <span style={{ fontSize: "11px", color: "#8A8C9A", marginLeft: "14px" }}>{t("widget", "K", locale)} = {String(Number(1000).toLocaleString(locale == "ar" ? "ar-EG" : "en"))}</span>}
                  {rateLegend.includes("m") && destinationType.type == "2" && <span style={{ fontSize: "11px", color: "#8A8C9A", marginLeft: "14px" }}>{t("widget", "M", locale)} = {String(Number(1000000).toLocaleString(locale == "ar" ? "ar-EG" : "en"))}</span>}
                  {demandData.length > 0 &&
                    <div style={{ display: "flex", marginLeft: "14px" }}>
                      <span style={{ fontSize: "11px", color: "#EB2727" }}>{t("widget", "highDemand", locale)}</span>
                      <span style={{ fontSize: "11px", color: "#8A8C9A", marginLeft: "3px" }}>{t("widget", "dates", locale)}</span>
                    </div>
                  }
                  {barData.length > 0 &&
                    <div style={{ display: "flex" }}>
                      <div style={{ width: "12px", height: "12px", backgroundColor: "#EBEBEB", borderRadius: "4px", marginTop: "1px" }} />
                      <span style={{ fontSize: "11px", color: "#8A8C9A", marginLeft: "4px" }}>{t("widget", "soldOut", locale)}</span>
                    </div>
                  }
                </div>
              </Paper>
            }
          </Popper>
        }
      </Box>
    </Box>
  );
}
export default RangeDateControl